import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isPageRefreshed,
  useNavigateAfterLogout,
} from '../../utils/navigationUtils';
import { Paths } from '../../routes/paths';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  HStack,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCompanyStore } from '../../stores/companyStore';
import {
  emailLink,
  phoneLink,
  telegramLink,
  whatsAppLink,
} from '../../utils/communicationUtils';
import { isEmptyOrNull } from '../../utils/textUtils';
import {
  faSquareWhatsapp,
  faTelegram,
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthStore } from '../../stores/authStore';
import { AuthStatus } from '../../types/enums/AuthStatus';
import CommonLayout from '../layouts/CommonLayout';
import { useModuleStore } from '../../stores/moduleStore';
import { useLogoutSuccess } from '../../utils/authUtils';
import { StoreCustomerService } from '../../services/storeCustomerService';
import { AuthService } from '../../services/authService';
import useLoaderOverlay, { useToastMessage } from '../../utils/uiUtils';
import FullScreenLoaderOverlay from '../components/common/FullScreenLoaderOverlay';

const AccountBlockedPage: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompanyStore();
  const contactEmail = !isEmptyOrNull(company?.supportEmail)
    ? company?.supportEmail
    : company?.email;
  const isAuthenticated = useAuthStore(
    (state) => state.status == AuthStatus.Authenticated
  );
  const { unblockUser } = useAuthStore();

  const { t } = useTranslation(['auth', 'common']);

  const { overlayLoading, showOverlay, hideOverlay } = useLoaderOverlay();
  const { showErrorMessage } = useToastMessage();
  const authService = new AuthService();
  const customerService = new StoreCustomerService();
  const { logoutSuccess } = useLogoutSuccess();
  const { data: modules } = useModuleStore();
  const { navigateAfterLogout } = useNavigateAfterLogout();

  useEffect(() => {
    if (isPageRefreshed()) {
      navigate(Paths.Root);
    }
  }, []);

  const onSubmitLogout = async () => {
    showOverlay();
    try {
      const response = await authService.logout();
      if (modules?.store) {
        await customerService.logout();
      }
      hideOverlay();

      logoutSuccess(response);
      navigateAfterLogout();
    } catch (error) {
      hideOverlay();
      showErrorMessage(t('auth:logoutError'));
    }
  };

  return (
    <>
      <FullScreenLoaderOverlay isOpen={overlayLoading} />
      <CommonLayout showLogo={false}>
        <VStack gap="0">
          <Image
            w="160px"
            src={`${process.env.PUBLIC_URL}/assets/images/denied.svg`}
          />
          <Box h="30px"></Box>
          <Text fontSize="18px" fontWeight="500">
            {t('common:accountBlockedHeading')}
          </Text>
          <Box h="15px"></Box>
          <Text fontSize="15px">
            {t('common:accountBlockedContent1')}{' '}
            <Link
              fontWeight="500"
              color="primary.500"
              href={emailLink(contactEmail!)}
            >
              {contactEmail}
            </Link>{' '}
            {t('common:accountBlockedContent2')}{' '}
            <Link
              fontWeight="500"
              color="primary.500"
              href={phoneLink(company?.phone!)}
            >
              {company?.phone}
            </Link>{' '}
            {t('common:accountBlockedContent3')}
          </Text>
          <Box h="25px"></Box>
          <HStack spacing="25px">
            <Link href={whatsAppLink(company?.phone!)} isExternal>
              <FontAwesomeIcon
                color="#128C7E"
                size="3x"
                icon={faSquareWhatsapp}
              />
            </Link>
            <Link href={telegramLink(company?.phone!)} isExternal>
              <FontAwesomeIcon color="#0088cc" size="3x" icon={faTelegram} />
            </Link>
          </HStack>
          <Box h="35px"></Box>
          <Button
            w="100%"
            colorScheme="primary"
            onClick={() => {
              if (isAuthenticated) {
                unblockUser();
              }
              navigate(Paths.Root, {
                state: { redirectedFrom: 'accountBlocked' },
              });
            }}
          >
            {t('common:tryAgain')}
          </Button>
          {isAuthenticated && (
            <>
              <Box h="15px"></Box>
              <Button
                w="100%"
                colorScheme="primary"
                variant="outline"
                onClick={onSubmitLogout}
              >
                {t('auth:logout')}
              </Button>
            </>
          )}
        </VStack>
      </CommonLayout>
    </>
  );
};

export default AccountBlockedPage;
